import { useAppSelector } from 'app/hooks';
import DuctedPairLinkSelect from 'components/configuration-page/ducted-pair-link-select';
import { ConfigurationType } from 'data/types';
import { useMemo } from 'react';
import {
  selectDuctedPairLinkMode,
  selectEligibleDuctedSystems
} from 'utils/ducted-design';
import { DuctedPairLinkMode } from 'utils/ducted-design-types';
import { linkedExistingSystemIds } from 'utils/ducted-pair-links';
import {
  buildDuctedPairSummaries,
  describeDuctedPairStatus,
  describeUnpairedDuctedSystems,
  getDuctedPairTarget,
  isDuctedPairLinkable
} from 'utils/ducted-pair-summary';
import { DuctedPairSummary } from 'utils/ducted-pair-summary-types';
import { abbreviateNumber } from 'utils/helpers';

type DuctedPairsSummaryCardProps = Readonly<{
  configuration: ConfigurationType;
  readOnly?: boolean;
}>;

type DuctedPairRowProps = Readonly<{
  configuration: ConfigurationType;
  pair: DuctedPairSummary;
  readOnly: boolean;
  linkMode: DuctedPairLinkMode;
}>;

function DuctedPairRow({
  configuration,
  pair,
  readOnly,
  linkMode
}: DuctedPairRowProps) {
  const eligibleSystems = useAppSelector(selectEligibleDuctedSystems);
  const takenSystemIds = useMemo(
    () => linkedExistingSystemIds(configuration, pair.condenserId),
    [configuration, pair.condenserId]
  );
  const coverage =
    pair.roomCount === 0
      ? 'No rooms yet'
      : `${pair.roomCount} room${pair.roomCount === 1 ? '' : 's'}${
          pair.levelLabels.length > 0 ? ` · ${pair.levelLabels.join(', ')}` : ''
        }`;
  const statusCopy = describeDuctedPairStatus(pair.status);

  return (
    <li className='flex flex-col gap-2 rounded-md border border-gray-200 p-3'>
      <div className='flex items-start justify-between gap-2'>
        <div className='flex min-w-0 flex-col'>
          <span className='truncate text-sm font-semibold text-gray-900'>
            {pair.pairName}
          </span>
          <span className='text-xs text-gray-500'>{coverage}</span>
        </div>
        {pair.roomCount > 0 && (
          <span className='shrink-0 text-xs tabular-nums text-gray-600'>
            {abbreviateNumber(pair.coolingBtu)} /{' '}
            {abbreviateNumber(pair.heatingBtu)} BTU
          </span>
        )}
      </div>
      {linkMode === 'manual' && (
        <DuctedPairLinkSelect
          configurationId={configuration.uniqueId}
          condenserId={pair.condenserId}
          value={pair.existingSystemId}
          eligibleSystems={eligibleSystems}
          takenSystemIds={takenSystemIds}
          disabled={readOnly || !isDuctedPairLinkable(pair.status)}
        />
      )}
      {statusCopy !== null && (
        <span className='text-xs text-amber-700'>{statusCopy}</span>
      )}
    </li>
  );
}

export default function DuctedPairsSummaryCard({
  configuration,
  readOnly = false
}: DuctedPairsSummaryCardProps) {
  const { structure } = useAppSelector((state) => state.structure);
  const eligibleSystems = useAppSelector(selectEligibleDuctedSystems);
  const linkMode = useAppSelector(selectDuctedPairLinkMode);
  const pairs = useMemo(
    () =>
      buildDuctedPairSummaries(
        configuration,
        structure,
        new Set(eligibleSystems.map((system) => system.uniqueId)),
        linkMode
      ),
    [configuration, structure, eligibleSystems, linkMode]
  );
  const unpairedCopy = useMemo(
    () =>
      describeUnpairedDuctedSystems(configuration, eligibleSystems, linkMode),
    [configuration, eligibleSystems, linkMode]
  );
  const readyCount = pairs.filter(
    (pair) => pair.status.kind === 'ready'
  ).length;
  const pairTarget = getDuctedPairTarget(
    pairs.length,
    eligibleSystems.length,
    linkMode
  );

  return (
    <div className='flex shrink-0 flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4'>
      <div className='flex items-center justify-between'>
        <span className='text-sm font-semibold text-gray-900'>
          Ducted Systems
        </span>
        <span className='text-xs tabular-nums text-gray-500'>
          {readyCount}/{pairTarget} ready
        </span>
      </div>
      {pairs.length === 0 && unpairedCopy === null && (
        <span className='text-xs text-gray-500'>
          Place a condenser to start your first ducted system.
        </span>
      )}
      {pairs.length > 0 && (
        <ul className='flex flex-col gap-2'>
          {pairs.map((pair) => (
            <DuctedPairRow
              key={pair.condenserId}
              configuration={configuration}
              pair={pair}
              readOnly={readOnly}
              linkMode={linkMode}
            />
          ))}
        </ul>
      )}
      {unpairedCopy !== null && (
        <span className='text-xs text-amber-700'>{unpairedCopy}</span>
      )}
    </div>
  );
}
